import React from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Dialog,
  Paper,
  DialogTitle,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import AddReceiptsReviewTable from "./AddReceiptsReviewTable";
import AddReceiptsMethodSelection from "./AddReceiptsMethodSelection";
import AddReceiptsUploadFile from "./AddReceiptsUploadFile";
import AddReceiptsManualInput from "./AddReceiptsManualInput";
import { ReceiptsEndPointLogic } from "../../../../../logic/Finance/ProviderExpense/ReceiptsEndpointLogic";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../../../../features/userSlice";
import { CircularProgress } from "@mui/material";
import {
  resetReceiptsForm,
  setManualReceiptsPayload,
  setUploadReceiptsPayload,
} from "../../../../../features/financeReceiptSlice";
import { useDispatch } from "react-redux";
import ModalSkeleton from "../helpers/components/ModalSkeleton";
import { ParingRateQueryID } from "../helpers/funcs/staticQID";

interface AddReceiptsProps {
  open: boolean;
  handleClose: () => void;
}

const tableHeadings = {
  company: "Company",
  receipt_number: "Receipt Number",
  receipt_amount: "Receipt Amount",
  finance_company_id: "Finance Company ID",
};

const AddReceiptsModal = ({ open, handleClose }: AddReceiptsProps) => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [uploadedFiles, setUploadedFiles] = React.useState<any[]>([]);
  const [parsedRows, setParsedRows] = React.useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const dispatch = useDispatch();
  const {
    manualReceiptsPayload,
    uploadReceiptsPayload,
    allCompanies,
    allCurrencies,
    loadingModal,
    selectedReceiptDateRange,
  } = useSelector((state: any) => state.receipts);
  const from_date = selectedReceiptDateRange[0]?.format("YYYY-MM-DD");
  const to_date = selectedReceiptDateRange[1]?.format("YYYY-MM-DD");
  const isManualFormValid = React.useMemo(() => {
    if (
      !Array.isArray(manualReceiptsPayload) ||
      manualReceiptsPayload.length === 0
    ) {
      return false;
    }

    return manualReceiptsPayload.every((r: any) => {
      return (
        r.receipt_id &&
        r.receipt_id.trim() !== "" &&
        r.receipt_date &&
        r.receipt_amount !== null &&
        !Number.isNaN(r.receipt_amount) &&
        r.receipt_amount > 0 &&
        r.receipt_currency_shared_uid &&
        r.company_shared_uid &&
        r.receipt_status
      );
    });
  }, [manualReceiptsPayload]);
  const hasDuplicateReceiptNumbers = React.useMemo(() => {
    const values = manualReceiptsPayload
      .map((r: any) => r.receipt_id)
      .filter(Boolean);

    return new Set(values).size !== values.length;
  }, [manualReceiptsPayload]);

  const isUploadDataValid = React.useMemo(() => {
    if (
      !Array.isArray(uploadReceiptsPayload) ||
      uploadReceiptsPayload.length === 0
    ) {
      return false;
    }

    return uploadReceiptsPayload.every((r: any) => {
      return (
        r.receipt_id &&
        r.receipt_id.trim() !== "" &&
        r.receipt_date &&
        r.receipt_amount !== null &&
        !Number.isNaN(r.receipt_amount) &&
        r.receipt_amount > 0 &&
        r.receipt_currency_shared_uid &&
        r.company_shared_uid
      );
    });
  }, [uploadReceiptsPayload]);
  const steps = ["Method Selection", "Input", "Review & Save"];
  const resetModalState = () => {
    setActiveStep(0);
    setMethod("uploadFile");
    setUploadedFiles([]);
    setParsedRows([]);
  };

  const [activeStep, setActiveStep] = React.useState(0);
  const [method, setMethod] = React.useState<"uploadFile" | "manual_input">(
    "uploadFile"
  );

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      handleSave();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      dispatch(setManualReceiptsPayload([]));
      dispatch(setUploadReceiptsPayload([]));
      dispatch(resetReceiptsForm());
      setUploadedFiles([]);
      setParsedRows([]);
    }
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSave = async () => {
    const basePayload =
      method === "manual_input" ? manualReceiptsPayload : uploadReceiptsPayload;
    const payloadToSend = {
      receipts: basePayload,
    };
    setIsSubmitting(true);
    try {
      const response = await ReceiptsEndPointLogic.createReceipt(
        oauthTokenHeader,
        payloadToSend
      );
      if (response && [201, 207].includes(response.status)) {
        dispatch(setManualReceiptsPayload([]));
        dispatch(setUploadReceiptsPayload([]));
        resetModalState();
        handleClose();
        await ReceiptsEndPointLogic.getAllReceiptData(
          oauthTokenHeader,
          from_date,
          to_date
        );
        await ReceiptsEndPointLogic.getPairingRate(
          oauthTokenHeader,
          ParingRateQueryID,
          from_date,
          to_date
        );
      }
    } catch (err) {
      console.error("Failed to save data", err);
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const reviewRows = React.useMemo(() => {
    const source =
      method === "manual_input" ? manualReceiptsPayload : uploadReceiptsPayload;
    if (!Array.isArray(source) || source.length === 0) return [];

    return source.map((r: any) => {
      const company = allCompanies?.find(
        (c: any) => c.shared_uid === r.company_shared_uid
      );
      const companyName =
        company?.company_name ?? r.finance_company_name ?? "-";
      const financeCompanyId =
        company?.finance_company_id ?? r.finance_company_id ?? "";
      const currency = allCurrencies?.find(
        (cur: any) => cur.shared_uid === r.receipt_currency_shared_uid
      );

      const currencySymbol = currency?.currency_symbol ?? "";

      return {
        company: companyName,
        receipt_number: r.receipt_id ?? "",
        receipt_amount:
          r.receipt_amount !== undefined && r.receipt_amount !== null
            ? `${r.receipt_amount.toString()}${
                currencySymbol ? ` ${currencySymbol}` : ""
              }`
            : "",
        finance_company_id: financeCompanyId,
      };
    });
  }, [
    method,
    manualReceiptsPayload,
    uploadReceiptsPayload,
    allCompanies,
    allCurrencies,
  ]);

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <AddReceiptsMethodSelection method={method} setMethod={setMethod} />
        );

      case 1:
        return method === "uploadFile" ? (
          <AddReceiptsUploadFile
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            parsedRows={parsedRows}
            setParsedRows={setParsedRows}
          />
        ) : (
          <AddReceiptsManualInput />
        );

      case 2:
        return (
          <AddReceiptsReviewTable
            tableHeadings={tableHeadings}
            rows={reviewRows}
          />
        );

      default:
        return "Unknown Step";
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
            Add New Receipt
          </DialogTitle>

          {loadingModal ? (
            <ModalSkeleton />
          ) : (
            <Box sx={{ width: "100%", mt: 2 }}>
              <Stepper
                sx={{
                  "& .MuiStepIcon-root": {
                    color: "rgba(0,0,0,0.3)", // default inactive
                  },
                  "& .MuiStepIcon-root.Mui-active": {
                    color: "secondary.main",
                  },
                  "& .MuiStepIcon-root.Mui-completed": {
                    color: "secondary.main",
                  },
                }}
                activeStep={activeStep}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Step Content */}
              <Box
                sx={{
                  my: 4,
                  px: 1,
                }}
              >
                {renderStepContent(activeStep)}
              </Box>

              {/* Buttons */}
            </Box>
          )}
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              disabled={activeStep === 0 || isSubmitting}
              onClick={handleBack}
              color="secondary"
              sx={{ mr: 1 }}
            >
              Back
            </Button>

            <Button
              color="secondary"
              variant="outlined"
              disabled={isSubmitting}
              onClick={() => {
                resetModalState();
                handleClose();
              }}
              sx={{ mr: 1 }}
            >
              CANCEL
            </Button>

            <Button
              variant="contained"
              color="secondary"
              onClick={handleNext}
              {...(activeStep === steps.length - 1 && {
                "data-tracker": method === "uploadFile" ? 583 : 584,
              })}
              disabled={
                isSubmitting ||
                loadingModal ||
                (activeStep === 1 &&
                  method === "manual_input" &&
                  (!isManualFormValid || hasDuplicateReceiptNumbers)) ||
                (activeStep === 1 &&
                  method === "uploadFile" &&
                  !isUploadDataValid)
              }
              endIcon={
                isSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : activeStep === steps.length - 1 ? (
                  <AddIcon sx={{ ml: "0.5" }} />
                ) : (
                  <ChevronRightIcon sx={{ ml: "0.5" }} />
                )
              }
            >
              {isSubmitting
                ? "Saving..."
                : activeStep === steps.length - 1
                ? "Add receipt"
                : "Next"}
            </Button>
          </Box>
        </Paper>
      </Dialog>
    </>
  );
};

export default AddReceiptsModal;
